/**
 * organizations.ts — API client for the Organizations resource.
 *
 * WEB-281.4 P2 tenancy FE. Route prefix: /organizations.
 * Mirrors OrganizationsController from backend (WEB-281.3).
 *
 * WEB-344 → снято в WEB-2090 п.6.4: getAnalyticsAggregate (сводка организации по старой формуле выручки), get и addAdmin по id — без вызывающих в кабинете
 * (kept for backward-compat; superseded by getOrgStats/getOrgDaily below,
 * WEB-537 checked there are no other consumers before leaving it in place).
 * WEB-536/537: добавлены getOrgStats + getOrgDaily — полный контракт для
 * пересобранного org-дашборда (чипсы + таблица проектов + график динамики),
 * тот же AggregatedRowDto/StatsResponse, что и project-scoped statisticsApi.
 */
import { api } from './client';
import { longRequest } from './requestTimeout';
import type { AggregatedRow, StatsResponse } from './statistics';
import type { SubscriptionOutcomesResponse } from './moneyAnalytics';
import type { Organization } from '@/types';
import type { components } from './generated/schema';

type TransferOwnershipDto = components['schemas']['TransferOwnershipDto'];
type DeleteOrgDto = components['schemas']['DeleteOrgDto'];

/**
 * WEB-3970 — повторное подтверждение передачи и удаления организации: ровно одно
 * из двух полей контракта (`password` у учётной записи с паролем, свежий токен
 * Google у вошедших только через Google). Второе поле в тело не попадает.
 */
export type OrgActionConfirmation =
  | { password: NonNullable<TransferOwnershipDto['password']>; googleAccessToken?: never }
  | {
      googleAccessToken: NonNullable<TransferOwnershipDto['googleAccessToken']>;
      password?: never;
    };

/** WEB-2806: ответ ручки лимита трат на ИИ — из сгенерированного контракта. */
export type OrgAiSpendLimits =
  components['schemas']['OrgAiSpendLimitsResponseDto'];
/** WEB-3969: ряд по дням для каждого проекта организации — из контракта. */
export type OrgProjectDailyResponse =
  components['schemas']['OrgProjectDailyResponseDto'];
/** WEB-3977: списки значений фишек UTM и «Страна» дашборда организации. */
export type OrgFilterOptionsResponse =
  components['schemas']['OrgFilterOptionsResponseDto'];
/** WEB-3741 «страны-деньги» — выручка организации по странам и валютам. */
export type OrgCountryRevenueResponse =
  components['schemas']['OrgCountryRevenueResponseDto'];
export type OrgCountryRevenueRow =
  components['schemas']['OrgCountryRevenueRowDto'];
/** WEB-2806: тело записи лимита трат на ИИ. */
export type UpdateOrgAiSpendLimits =
  components['schemas']['UpdateOrgAiSpendLimitsDto'];

export interface CreateOrganizationPayload {
  name: string;
}

// ---- WEB-343 follow-ups: members, rename, promote-by-email ----

export interface OrgMember {
  userId: string;
  /** null when the user relation is missing (defensive). */
  email: string | null;
  role: 'owner' | 'admin';
  createdAt: string;
  /**
   * WEB-1093 — доступ к платёжным данным. Owner всегда true (сервер не даёт
   * снять); для admin по умолчанию true (полный доступ), false — владелец
   * явно отключил.
   */
  billingAccess: boolean;
}

/** POST /admins и /admins/by-email отвечают этой формой. (Ранее userId
 * требовался для второго запроса PATCH .../billing-access из AddAdminModal —
 * той схемы больше нет, решение о доступе уходит самим приглашением;
 * PATCH остаётся для смены доступа из списка участников, см. OrgTeam.) */
export interface OrgAdminResponse {
  id: string;
  orgId: string;
  userId: string;
  role: 'owner' | 'admin';
  createdAt: string;
}

export interface OrgMembersListResponse {
  members: OrgMember[];
  total: number;
}

// ---- WEB-1562: удаление организации вместе с проектами ----

/** Что исчезнет вместе с одним проектом организации. */
export interface OrgDeletionProject {
  id: string;
  name: string;
  funnels: number;
  /** Из них опубликованных — перестанут открываться по публичным ссылкам. */
  publishedFunnels: number;
  quizzes: number;
  paywalls: number;
  members: number;
}

/** Помеха сносу: код для кабинета + человеческий текст с выполнимым выходом. */
export interface OrgDeletionBlocker {
  code: string;
  message: string;
}

export interface OrgDeletionPreview {
  orgId: string;
  orgName: string;
  /** Что человек должен ввести для подтверждения (правило задаёт сервер). */
  confirmationPhrase: string;
  canDelete: boolean;
  projects: OrgDeletionProject[];
  totals: {
    projects: number;
    funnels: number;
    publishedFunnels: number;
    quizzes: number;
    paywalls: number;
    members: number;
    domains: number;
  };
  domains: Array<{
    name: string;
    type: string;
    status: string;
    projectId: string | null;
  }>;
  buyers: { activeGrants: number; purchases: number; subscriptions: number };
  /**
   * Строк журнала операций, которые ПЕРЕЖИВУТ снос обезличенно: сама запись
   * о движении денег остаётся, ссылка на организацию обнуляется (решение
   * владельца 26.08 по WEB-1562). Кабинет обязан сказать об этом человеку —
   * умолчать значило бы намекнуть, что след платежей стирается.
   */
  ledgerRowsPreserved: number;
  blockers: OrgDeletionBlocker[];
}

// ---- WEB-536/537: org-dashboard stats/daily (chips + table + chart) ----

/**
 * Разрезы статистики организации, которые зовёт кабинет: итоги (`none`),
 * строки проектов (`project`) и «Посетители по странам» дашборда
 * (`country`, WEB-3741; сервер и контракт принимают — OrgGroupBy).
 */
export type OrgAnalyticsGroupBy = 'none' | 'project' | 'country';

/**
 * Query params for GET /organizations/:orgId/analytics/stats and
 * GET /organizations/:orgId/analytics/daily. WEB-2609 п.1: пояса `tz` здесь
 * нет — его принимают только /daily и /daily-by-project
 * (`OrgDailyQueryParams`), прочие ручки на лишнее поле отвечают 400. Mirrors the backend
 * OrgAnalyticsQueryDto (src/organizations/dto/org-analytics-stats.dto.ts) —
 * `projectId` is deliberately absent (org scope has many projects);
 * `projectIds` narrows to a subset, intersected server-side with the org's
 * own projects.
 */
export interface OrgAnalyticsQueryParams {
  from?: string;
  to?: string;
  /** stats endpoint only — ignored by /daily. Default 'none' (totals only). */
  groupBy?: OrgAnalyticsGroupBy;
  projectIds?: string[];
  quizIds?: string[];
  paywallIds?: string[];
  countries?: string[];
  utmSource?: string;
  utmCampaign?: string;
  utmMedium?: string;
  /**
   * WEB-3977: значения UTM списками — фишки дашборда организации (сервер:
   * OrgAnalyticsQueryDto utmSources/utmCampaigns/utmMediums, список
   * перекрывает одиночное поле).
   */
  utmSources?: string[];
  utmCampaigns?: string[];
  utmMediums?: string[];
  memberIds?: string[];
  /** WEB-3994: фишки «Воронка» и «Домен» дашборда организации. */
  funnelIds?: string[];
  domainIds?: string[];
  variantIds?: string[];
  smartLinkIds?: string[];
  // WEB-741 Фаза 2 FE (F-4): query param mirrors backend
  // OrgAnalyticsQueryDto.includeTestPayments (organizations.controller.ts) —
  // undefined/false = default (test-mode Stripe payments excluded).
  includeTestPayments?: boolean;
  /**
   * WEB-2404 Г3: явное «Обновить» — сервер считает мимо кэша отчётов и
   * перезаписывает его (OrgAnalyticsQueryDto.fresh). Обычная загрузка поле
   * не шлёт: иначе кэш дашборда перестал бы работать вовсе.
   */
  fresh?: boolean;
}

/**
 * WEB-2609 п.1 — запрос /daily и /daily-by-project: общий запрос плюс пояс
 * отчёта (сервер: OrgDailyQueryDto).
 */
export type OrgDailyQueryParams = Omit<OrgAnalyticsQueryParams, 'groupBy'> & {
  /** WEB-2404 Г1: пояс отчёта (reportTimezone) — сутки графика организации. */
  tz?: string;
};

/**
 * WEB-2526 — запрос /stats: общий запрос плюс признак «без Итого» (сервер:
 * OrgStatsQueryDto.skipTotals) — для запроса, из которого берутся одни строки.
 */
export type OrgStatsQueryParams = OrgAnalyticsQueryParams & {
  skipTotals?: boolean;
};

/**
 * WEB-2420 Д-10: необязательный сигнал отмены — дашборд снимает устаревший
 * набор запросов при смене периода/фильтров. Не передан — настройки запроса
 * прежние (ключа `signal` нет).
 */
const withSignal = (params: unknown, signal?: AbortSignal) =>
  signal ? { params, signal } : { params };

export const organizationsApi = {
  /**
   * WEB-2806: лимит трат организации на ИИ в сутки и в месяц и траты за
   * окно — в центах (WEB-3808: `dailyLimitCents` … `largeLaunchThresholdCents`).
   * Читают владелец и администратор организации.
   */
  getAiSpendLimits: (orgId: string) =>
    api.get<OrgAiSpendLimits>(`/organizations/${orgId}/ai-spend-limits`),

  /**
   * WEB-2806: заменить оба лимита (null — лимит снят). Сервер пускает только
   * того, у кого есть доступ к деньгам организации (canManageBilling), прочим
   * — 403 AI_SPEND_LIMIT_BILLING_ACCESS_REQUIRED.
   */
  setAiSpendLimits: (orgId: string, body: UpdateOrgAiSpendLimits) =>
    api.put<OrgAiSpendLimits>(`/organizations/${orgId}/ai-spend-limits`, body),

  /** GET /organizations — list orgs the authenticated user belongs to. */
  list: () => api.get('/organizations'),

  /** POST /organizations — create a new organization (caller becomes Owner). */
  create: (data: CreateOrganizationPayload) => api.post('/organizations', data),

  /**
   * POST /organizations/:orgId/admins/by-email — пригласить администратора.
   *
   * WEB-1093: решение о доступе к платёжным данным уходит этим же запросом.
   * Раньше приходилось слать два (пригласить, затем снять доступ), и в
   * промежутке администратор фактически имел полный доступ к деньгам.
   * Не передано — полный доступ, как работало всегда.
   */
  addAdminByEmail: (orgId: string, email: string, billingAccess?: boolean) =>
    api.post<OrgAdminResponse>(`/organizations/${orgId}/admins/by-email`, {
      email,
      ...(billingAccess === false ? { billingAccess: false } : {}),
    }),

  /** GET /organizations/:orgId/members — list members (Owner/Admin only). */
  getMembers: (orgId: string) =>
    api.get<OrgMembersListResponse>(`/organizations/${orgId}/members`),

  /**
   * PATCH /organizations/:orgId/members/:userId/billing-access — WEB-1093.
   * Owner-only. Grants/revokes an admin's access to billing data after the
   * fact, without re-inviting. 409 if userId is the org owner (server-side
   * guard — the owner can never be revoked; the UI must not offer the
   * control on the owner's own row either, see OrgTeam.tsx).
   */
  setBillingAccess: (orgId: string, userId: string, billingAccess: boolean) =>
    api.patch<OrgAdminResponse>(
      `/organizations/${orgId}/members/${userId}/billing-access`,
      { billingAccess },
    ),

  /**
   * POST /organizations/:orgId/leave — leave the organization (WEB-508).
   * Available to Owner or Admin per @OrgRoles, but the BE rejects (409) an
   * Owner attempt — transfer ownership first. Not a member -> 403.
   */
  leave: (orgId: string) => api.post(`/organizations/${orgId}/leave`),

  /**
   * DELETE /organizations/:orgId/admins/:userId — Owner demotes an Admin.
   * Org-level has no explicit "member" role: this removes the target's
   * organization_members row entirely (WEB-508). Owner-only; 409 if the
   * target is the Owner (use transferOwnership instead).
   */
  demoteAdmin: (orgId: string, userId: string) =>
    api.delete(`/organizations/${orgId}/admins/${userId}`),

  /**
   * DELETE /organizations/:orgId/members/:userId — Owner removes a member from
   * the organization entirely: strips the org role AND all project memberships
   * in the org's projects, in one transaction (WEB-701). Owner-only; 409 if the
   * target is the Owner; 403 for self-target (use leave/transferOwnership).
   */
  removeMember: (orgId: string, userId: string) =>
    api.delete(`/organizations/${orgId}/members/${userId}`),

  /**
   * POST /organizations/:orgId/transfer-ownership — atomically hand
   * ownership to an existing org member (Owner-only, WEB-508). The caller
   * becomes Admin on success. WEB-3970: тело несёт повторное подтверждение —
   * пароль либо токен Google (`OrgActionConfirmation`).
   */
  transferOwnership: (
    orgId: string,
    newOwnerId: string,
    confirm: OrgActionConfirmation,
  ) => {
    const body: TransferOwnershipDto = { newOwnerId, ...confirm };
    return api.post<Organization>(
      `/organizations/${orgId}/transfer-ownership`,
      body,
    );
  },

  /** PATCH /organizations/:orgId — rename the organization (Owner-only). */
  rename: (orgId: string, name: string) =>
    api.patch(`/organizations/${orgId}`, { name }),

  /**
   * DELETE /organizations/:orgId — удаляет организацию ВМЕСТЕ с проектами
   * (WEB-1562, решение владельца 25.08). Подтверждение — название
   * организации слово в слово; без него сервер отвечает 400. Помехи (чужие
   * деньги, домены) сервер по-прежнему отдаёт как 409 с человеческим
   * текстом — показывать его как есть, своих советов не выдумывать.
   * WEB-3970: вместе с названием — повторное подтверждение (пароль либо токен
   * Google, `OrgActionConfirmation`).
   */
  delete: (
    orgId: string,
    dto: { confirmName: string } & OrgActionConfirmation,
  ) => {
    const body: DeleteOrgDto = dto;
    return api.delete(`/organizations/${orgId}`, { data: body });
  },

  /**
   * GET /organizations/:orgId/deletion-preview — WEB-1562.
   * Что именно исчезнет вместе с организацией и что удалению мешает.
   * Кабинет ОБЯЗАН показать это до подтверждения: человек подтверждает
   * перечень, а не «вы уверены?».
   */
  deletionPreview: (orgId: string) =>
    api.get<OrgDeletionPreview>(`/organizations/${orgId}/deletion-preview`),

  /**
   * GET /organizations/:orgId/analytics/stats
   * WEB-536/537 — full dashboard contract. groupBy omitted/'none' -> `totals`
   * is the org-wide aggregate for the SUMMARY chips (§4). groupBy='project'
   * -> `rows` is one row per project (dimension=project_id,
   * dimensionLabel=project name) for the PROJECTS table (§6).
   */
  getOrgStats: (
    orgId: string,
    params?: OrgStatsQueryParams,
    signal?: AbortSignal,
  ) =>
    api.get<StatsResponse>(
      `/organizations/${orgId}/analytics/stats`,
      { ...withSignal(params, signal), ...longRequest('orgStats') },
    ),

  /**
   * GET /organizations/:orgId/analytics/daily
   * WEB-536/537 — one row per day (dimension='YYYY-MM-DD') for the trend
   * chart (§5). groupBy is not accepted by this endpoint (BE ignores it).
   */
  getOrgDaily: (
    orgId: string,
    params?: OrgDailyQueryParams,
    signal?: AbortSignal,
  ) =>
    api.get<StatsResponse>(
      `/organizations/${orgId}/analytics/daily`,
      { ...withSignal(params, signal), ...longRequest('orgDaily') },
    ),

  /**
   * GET /organizations/:orgId/analytics/daily-by-project
   * WEB-3969 — per-day series for every project of the selection in one
   * request (the «Trend» column of the organization «Projects» page). Same
   * query as getOrgDaily; days without events are absent (fill on the client).
   */
  getOrgDailyByProject: (
    orgId: string,
    params?: OrgDailyQueryParams,
    signal?: AbortSignal,
  ) =>
    api.get<OrgProjectDailyResponse>(
      `/organizations/${orgId}/analytics/daily-by-project`,
      { ...withSignal(params, signal), ...longRequest('orgDailyByProject') },
    ),

  /**
   * GET /organizations/:orgId/analytics/subscription-outcomes
   * WEB-3772 юнит 2 — пробный период и причины ухода по журналу подписок
   * проектов организации (или выбранных `projectIds`); тот же ответ, что у
   * проекта (`moneyAnalyticsApi.subscriptionOutcomes`).
   */
  subscriptionOutcomes: (
    orgId: string,
    params?: Omit<OrgAnalyticsQueryParams, 'groupBy'>,
    signal?: AbortSignal,
  ) =>
    api.get<SubscriptionOutcomesResponse>(
      `/organizations/${orgId}/analytics/subscription-outcomes`,
      { ...withSignal(params, signal), ...longRequest('orgSubscriptionOutcomes') },
    ),

  /**
   * GET /organizations/:orgId/analytics/country-revenue
   * WEB-3741 «страны-деньги» — выручка организации по паре «страна × валюта»
   * с продлениями, возвратами и проигранными спорами (блок «Выручка по
   * странам»); суммы разных валют не складываются.
   */
  getOrgCountryRevenue: (
    orgId: string,
    params?: Omit<OrgAnalyticsQueryParams, 'groupBy'>,
    signal?: AbortSignal,
  ) =>
    api.get<OrgCountryRevenueResponse>(
      `/organizations/${orgId}/analytics/country-revenue`,
      { ...withSignal(params, signal), ...longRequest('orgCountryRevenue') },
    ),

  /**
   * GET /organizations/:orgId/analytics/filter-options
   * WEB-3977 — value lists for the UTM and «Country» chips of the organization
   * dashboard (distinct values from the events of the org projects, all
   * time). Not a report: no period, no long-request ceiling.
   */
  getOrgFilterOptions: (
    orgId: string,
    params?: { projectIds?: string[]; fresh?: boolean },
    signal?: AbortSignal,
  ) =>
    api.get<OrgFilterOptionsResponse>(
      `/organizations/${orgId}/analytics/filter-options`,
      withSignal(params, signal),
    ),
};

export type { AggregatedRow, StatsResponse };
